import React, { useState, useEffect } from 'react';
import Modal from '../common/Modal';
import { Building2, Users, Save } from 'lucide-react';

const EditCompanyModal = ({ company, isOpen, onClose, onSubmit, loading }) => {
  const [formData, setFormData] = useState({
    name: '',
    industry: '',
    logo: '',
    employeeLimit: 50,
    status: 'Active',
    enableFaceAttendance: true,
    enableQrScan: true,
    enableInstantAttendance: true
  });

  useEffect(() => {
    if (company) {
      setFormData({
        name: company.name || '',
        industry: company.industry || '',
        logo: company.logo || '',
        employeeLimit: company.employeeLimit || 50,
        status: company.status || 'Active',
        enableFaceAttendance: company.enableFaceAttendance !== undefined ? company.enableFaceAttendance : true,
        enableQrScan: company.enableQrScan !== undefined ? company.enableQrScan : true,
        enableInstantAttendance: company.enableInstantAttendance !== undefined ? company.enableInstantAttendance : true
      });
    }
  }, [company]);

  if (!isOpen || !company) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (name === 'employeeLimit' ? Number(value) : value)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(company._id, formData);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Edit Company Settings: ${company.name}`} maxWidth="max-w-md">
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Company Summary Banner */}
        <div className="flex items-center space-x-3 p-3 rounded-2xl bg-white border border-slate-100">
          <img
            src={company.logo || 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=150&auto=format&fit=crop&q=80'}
            alt={company.name}
            className="w-12 h-12 rounded-xl object-cover border border-slate-700"
          />
          <div>
            <h4 className="text-sm font-bold text-slate-900 font-display">{company.name}</h4>
            <p className="text-xs text-slate-500">{company.industry || 'Company Tenant'}</p>
          </div>
        </div>

        {/* Attendance Verification Methods */}
        <div className="p-3.5 rounded-2xl bg-white border border-slate-100 space-y-2.5">
          <label className="block text-xs font-bold text-green-700 uppercase tracking-wider">
            Attendance Verification Methods
          </label>
          <div className="space-y-2">
            <label className={`flex items-center justify-between p-2.5 rounded-xl border transition cursor-pointer ${formData.enableFaceAttendance ? 'bg-green-950/40 border-green-600/40' : 'bg-white/60 border-slate-100 opacity-60'}`}>
              <div>
                <p className="text-xs font-bold text-slate-700">Face Recognition</p>
                <p className="text-[10px] text-slate-500">AI camera face matching</p>
              </div>
              <input
                type="checkbox"
                name="enableFaceAttendance"
                checked={formData.enableFaceAttendance}
                onChange={handleChange}
                className="w-4 h-4 rounded text-green-700 focus:ring-green-600 bg-white border-slate-700"
              />
            </label>

            <label className={`flex items-center justify-between p-2.5 rounded-xl border transition cursor-pointer ${formData.enableQrScan ? 'bg-green-950/40 border-green-600/40' : 'bg-white/60 border-slate-100 opacity-60'}`}>
              <div>
                <p className="text-xs font-bold text-slate-700">QR Code Scanner</p>
                <p className="text-[10px] text-slate-500">Office QR code scan</p>
              </div>
              <input
                type="checkbox"
                name="enableQrScan"
                checked={formData.enableQrScan}
                onChange={handleChange}
                className="w-4 h-4 rounded text-green-700 focus:ring-green-600 bg-white border-slate-700"
              />
            </label>

            <label className={`flex items-center justify-between p-2.5 rounded-xl border transition cursor-pointer ${formData.enableInstantAttendance ? 'bg-green-950/40 border-green-600/40' : 'bg-white/60 border-slate-100 opacity-60'}`}>
              <div>
                <p className="text-xs font-bold text-slate-700">Instant Check In/Out</p>
                <p className="text-[10px] text-slate-500">1-Tap GPS verified check-in</p>
              </div>
              <input
                type="checkbox"
                name="enableInstantAttendance"
                checked={formData.enableInstantAttendance}
                onChange={handleChange}
                className="w-4 h-4 rounded text-green-700 focus:ring-green-600 bg-white border-slate-700"
              />
            </label>
          </div>
        </div>

        {/* Employee Limit Increase Section */}
        <div className="p-4 rounded-2xl bg-green-950/40 border border-green-600/30 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 text-xs font-bold text-green-400">
              <Users className="w-4 h-4 text-green-700" />
              <span>Employee Count Limit</span>
            </div>
            <span className="text-xs text-slate-600 font-medium">
              Current Employees: <strong className="text-emerald-600">{company.currentEmployeeCount || 0}</strong>
            </span>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-900 mb-1.5">Max Employee Limit Cap *</label>
            <input
              type="number"
              name="employeeLimit"
              min={company.currentEmployeeCount || 1}
              required
              value={formData.employeeLimit}
              onChange={handleChange}
              className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-green-600/50 text-sm font-bold text-green-400 focus:outline-none focus:border-green-500 transition"
            />
            <p className="text-[11px] text-slate-500 mt-1.5">
              Enter the new total employee limit for this company tenant.
            </p>
          </div>
        </div>

        {/* Optional Basic Details */}
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">Company Name</label>
            <input
              type="text"
              name="name"
              required
              value={formData.name}
              onChange={handleChange}
              className="w-full px-3.5 py-2 rounded-xl bg-white border border-slate-100 text-xs sm:text-sm text-slate-700 focus:outline-none focus:border-green-600 transition"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">Tenant Status</label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full px-3.5 py-2 rounded-xl bg-white border border-slate-100 text-xs sm:text-sm text-slate-700 focus:outline-none focus:border-green-600 transition"
            >
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
              <option value="Suspended">Suspended</option>
            </select>
          </div>
        </div>

        <div className="flex items-center justify-end space-x-3 pt-4 border-t border-slate-100">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 rounded-xl text-xs font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-900 transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-5 py-2 rounded-xl text-xs font-semibold text-white bg-green-700 hover:bg-green-600 shadow-lg shadow-green-700/30 flex items-center space-x-1.5 transition disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            <span>{loading ? 'Saving...' : 'Update Limit Count'}</span>
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default EditCompanyModal;
